/** @format */

import React, {useContext, useState} from 'react';
import styles from './Wallet.module.css';
import {SupportEmail} from '../../common/SupportFooter';
import {Header} from '../../common/Header';
import {AmountSection} from '../../common/AmountSection';
import {RoutingContext} from '../../../context/Router';
import {MobileWrapper} from '../../common/MobileWrapper';
import {Login} from './screens/Login';
import {Details} from './screens/Details';
import {sendMessage} from '../../../context/utils';
import {StatusMessage} from '../../StatusMessage';
import {BackToMethods} from '../../common/BackToMethods';
import {StatusContext} from '../../../context/Status';
import {ErrorAlertContext} from '../../../context/ErrorAlert';
import axios from 'axios';
import {Otp} from "../Card/screens/Otp";
import Loader from "../../common/Loader";

export type WalletPayProps = {
    // username: string, password: string
    [x: string]: any;
};

export const BusinessWallet = () => {
    const {email, currency, amount, merchantKey, payData} =
        useContext(RoutingContext);
    const {setPageTransactionStatus} = useContext(StatusContext);

    const {onShowAlert} = useContext(ErrorAlertContext);

    const [userEmail, setUserEmail] = useState('');
    const [fullname, setFullname] = useState('');
    const [tabDisplayed, setTabDisplayed] = useState('login');
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    let mailformat = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/;


    const genericBusinessOTP = async (
        email: string,
        password: string,
        setTabDisplayed: (tab: string) => void
    ) => {
        try {
            const response = await axios(
                `${process.env.REACT_APP_API_URL}auth/account/one/time/password`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'x-auth-token': merchantKey,
                        ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                            'x-fe-token': process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN,
                        }),
                    },
                    data: {
                        email,
                        password,
                    },
                }
            );


            if (response.status === 200) {
                setTabDisplayed('otp');
            } else {
                throw new Error(`OTP failed: ${response.status}`);
            }
        } catch (error: any) {
            console.error('OTP request failed:', error?.response?.status, error?.response?.data);
            throw error;
        }
    };


    const onPay = async (data?: WalletPayProps) => {
        if (!data?.username.match(mailformat) || !data?.username) {
            setEmailError('Please enter a valid email address');
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios(`${process.env.REACT_APP_API_URL}pay/wallet/popup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'x-auth-token': merchantKey,
                    ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                        "x-fe-token": process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN
                    })
                },
                data,
            });

            const responseData = response.data;

            if (responseData.status !== 'error') {
                try {
                    await genericBusinessOTP(responseData.email, data.password, setTabDisplayed);
                } catch (error) {
                    onShowAlert(error, 'Failed to send OTP. Please try again.');
                }

                setUserEmail(responseData.email);
                setFullname(`${responseData.firstName} ${responseData.lastName}`);
            }
        } catch (err: any) {
            if (err.response?.data?.error === 'Bad credentials') {
                onShowAlert(err, 'Wrong username or password');
            } else {
                onShowAlert(err, err.response?.data?.message || err.response?.data?.error);
            }
        } finally {
            setIsLoading(false);
        }
    };


    const onOtpSubmit = async (otpValue: string) => {
        try {
            setIsLoading(true);

            await axios.get(
                `${process.env.REACT_APP_API_URL}auth/user/email/confirmToken/${otpValue}/${userEmail}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "x-auth-token": merchantKey,
                        ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                            "x-fe-token": process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN,
                        }),
                    }
                }
            );

            const payload = {
                email: userEmail,
                fullname: fullname,
                amount,
                currency,
                paymentType: payData.kit?.paymentType || "html-checkout",
                productType: payData.kit.productType ?? "COLLECTION",
                sourceAmount: payData.kit?.sourceAmount,
                phone_number: payData.kit?.phone_number,
                tx_ref: payData.kit?.tx_ref,
                rate: payData.details?.rate,
                sourceCurrency: payData.sourceCurrency,
                paylinkToken: payData.kit.paylinkToken,
            };

            const paymentResponse = await axios.post(
                `${process.env.REACT_APP_API_URL}pay/wallet/business/make/payment`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "x-auth-token": merchantKey,
                        ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                            "x-fe-token": process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN,
                        }),
                    }
                }
            );

            const paymentData = paymentResponse.data;

            if (paymentData.error || paymentData.status === "error") {
                setTabDisplayed("error");
                setPageTransactionStatus("error");
            } else {
                setTabDisplayed("success");
                setPageTransactionStatus("success");

                const sendData = {
                    status: "successful",
                    txnRef: paymentData.walletTnxId,
                    tnxRef: paymentData.walletTnxId,
                    tx_ref: paymentData.walletTnxId,
                    amount: paymentData.amount,
                };

                setTimeout(() => {
                    sendMessage(sendData);
                }, 1000);
            }
        } catch (e: any) {
            onShowAlert("", e.response?.data?.message || "OTP or payment failed.");
            setTabDisplayed("error");
            setPageTransactionStatus("error");

            setTimeout(() => {
                sendMessage({status: "ERROR", tnxRef: payData.kit.tx_ref});
            }, 5000);
        } finally {
            setIsLoading(false);
        }
    };

    if (tabDisplayed === 'error' || tabDisplayed === 'success') {
        return <StatusMessage type={tabDisplayed}/>;
    }

    return (
        <MobileWrapper>
            <div className={styles.container}>
                <div className={styles.main}>
                    <Header email={email}/>
                    <AmountSection
                        amount={amount}
                        currency={payData?.details?.currency}
                    />

                    {tabDisplayed === "otp" && (
                        <Otp onSubmit={onOtpSubmit} message={"Check your email for the otp"} />
                    )}

                    <Loader isLoading={isLoading} />

                    <div className={styles.body}>
                        {tabDisplayed === 'login' && (
                            <Login
                                onPay={onPay}
                                isLoading={isLoading}
                                emailError={emailError}
                                setEmailError={setEmailError}
                            />
                        )}
                        {tabDisplayed === 'details' && (
                            <Details
                                email={userEmail}
                                fullName={fullname}
                                // onContinue={onContinue}
                                isLoading={isLoading}
                            />
                        )}
                    </div>
                    <BackToMethods/>
                </div>
                <SupportEmail/>
            </div>
        </MobileWrapper>
    );
};
